import * as React from "react";
import { ArtifactBlock, BlockList, Button, type ArtifactStatus, type Block } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

const DOWNLOAD_URL = "https://files.example.com/q3-revenue-report.pdf";

// One streamed `artifact` block, rendered through the BlockRenderer registry
// exactly the way the FE chats receive it - no per-app wiring.
const artifactBlocks: Block[] = [
  {
    block_id: "blk_art_pdf",
    type: "artifact",
    props: {
      artifact_id: "art_pdf",
      name: "q3-revenue-report.pdf",
      mime: "application/pdf",
      size_bytes: 1_572_864,
      download_url: DOWNLOAD_URL,
      status: "ready",
      run_id: "run_8f21",
    },
  },
  {
    block_id: "blk_art_xlsx",
    type: "artifact",
    props: {
      artifact_id: "art_xlsx",
      name: "cohort-breakdown.xlsx",
      mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      size_bytes: 48_234,
      download_url: "https://files.example.com/cohort-breakdown.xlsx",
      status: "ready",
    },
  },
];

const STATUS_ORDER: ArtifactStatus[] = ["processing", "ready", "error"];

export function ArtifactBlockDemo() {
  // Cycle one card through the lifecycle so the slide-then-fade motion on the
  // status line is visible on a real transition.
  const [statusIdx, setStatusIdx] = React.useState(0);
  const status = STATUS_ORDER[statusIdx];

  return (
    <Section
      id="artifact-block"
      title="ArtifactBlock"
      description="Downloadable file card. Renderer for the K2 `artifact` block - props are the streamed `{ artifact_id, name, mime, size_bytes, download_url, status }`. The Download action is live at every status (download-while-processing); the status line plays the house slide-then-fade on a status change."
    >
      <VariantBlock label="The three statuses" caption="download stays live throughout">
        <div className="w-full max-w-md space-y-3">
          <ArtifactBlock
            artifact_id="art_proc"
            name="annual-render.mp4"
            mime="video/mp4"
            size_bytes={73_400_320}
            download_url="https://files.example.com/annual-render.mp4"
            status="processing"
          />
          <ArtifactBlock
            artifact_id="art_ready"
            name="q3-revenue-report.pdf"
            mime="application/pdf"
            size_bytes={1_572_864}
            download_url={DOWNLOAD_URL}
            status="ready"
          />
          <ArtifactBlock
            artifact_id="art_err"
            name="export-large.csv"
            mime="text/csv"
            size_bytes={9_812_345}
            download_url="https://files.example.com/export-large.csv"
            status="error"
            errorMessage="The export hit a row limit. The partial file is still downloadable."
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Live transition" caption="slide-first-then-fade on status change">
        <div className="w-full max-w-md space-y-4">
          <ArtifactBlock
            artifact_id="art_cycle"
            name="weekly-digest.pdf"
            mime="application/pdf"
            size_bytes={324_096}
            download_url={DOWNLOAD_URL}
            status={status}
            errorMessage="Generation failed on the worker. Retry from the run."
          />
          <div className="flex items-center gap-3 border-t border-border pt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setStatusIdx((i) => (i + 1) % STATUS_ORDER.length)}
            >
              Advance status
            </Button>
            <span className="text-xs text-muted-foreground">
              status: <span className="font-mono text-foreground">{status}</span>
            </span>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock label="No url yet" caption="processing job before the file URL streams in">
        <div className="w-full max-w-md space-y-3">
          <ArtifactBlock
            artifact_id="art_nourl"
            name="rendering-video.mp4"
            mime="video/mp4"
            status="processing"
          />
          <ArtifactBlock artifact_id="art_minimal" name="untitled.bin" status="ready" />
        </div>
      </VariantBlock>

      <VariantBlock label="Type icons" caption="derived from mime, then extension">
        <div className="grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
          <ArtifactBlock
            artifact_id="i1"
            name="diagram.png"
            mime="image/png"
            size_bytes={88_000}
            download_url={DOWNLOAD_URL}
            status="ready"
          />
          <ArtifactBlock
            artifact_id="i2"
            name="theme-song.mp3"
            mime="audio/mpeg"
            size_bytes={3_200_000}
            download_url={DOWNLOAD_URL}
            status="ready"
          />
          <ArtifactBlock
            artifact_id="i3"
            name="bundle.zip"
            mime="application/zip"
            size_bytes={12_400_000}
            download_url={DOWNLOAD_URL}
            status="ready"
          />
          <ArtifactBlock
            artifact_id="i4"
            name="config.json"
            mime="application/json"
            size_bytes={2_048}
            download_url={DOWNLOAD_URL}
            status="ready"
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Rendered via BlockRenderer"
        caption="the chats render artifact blocks automatically through the registry"
      >
        <div className="w-full max-w-md">
          <BlockList blocks={artifactBlocks} />
        </div>
      </VariantBlock>
    </Section>
  );
}
